import { useCallback, useEffect, useState } from 'react';

type Bounds = { min: number; max: number };

const getBounds = (minWidth: number): Bounds => {
  const max = Math.floor(window.innerWidth * 0.9);
  return { min: Math.min(minWidth, max), max };
};

const clamp = (value: number, { min, max }: Bounds) => Math.min(Math.max(value, min), max);

const readStored = (storageKey: string): number | null => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
};

const writeStored = (storageKey: string, value: number) => {
  try {
    window.localStorage.setItem(storageKey, String(value));
  } catch {
    // localStorage недоступен — ширина просто не запоминается
  }
};

/**
 * Ширина панели с границами от размера окна и запоминанием в localStorage.
 * setWidth(w) только меняет ширину (для drag); setWidth(w, true) ещё и сохраняет её.
 */
export const useResizableWidth = (storageKey: string, defaultWidth: number, minWidth: number) => {
  const [bounds, setBounds] = useState<Bounds>(() => getBounds(minWidth));
  const [width, setWidthState] = useState<number>(() =>
    clamp(readStored(storageKey) ?? defaultWidth, getBounds(minWidth)),
  );

  useEffect(() => {
    const onResize = () => {
      const next = getBounds(minWidth);
      setBounds(next);
      setWidthState((prev) => clamp(prev, next));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [minWidth]);

  const setWidth = useCallback(
    (w: number, persist = false) => {
      const next = clamp(Math.round(w), getBounds(minWidth));
      setWidthState(next);
      if (persist) writeStored(storageKey, next);
    },
    [storageKey, minWidth],
  );

  return { width, bounds, setWidth };
};
